import { cx } from '@emotion/css';
import { faFaceSmile } from '@fortawesome/free-regular-svg-icons';
import EmojiPicker, { EmojiClickData } from 'emoji-picker-react';
import {
  cloneElement,
  isValidElement,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useOutsideClick } from 'apps/shared/scripts/hooks';

import Button from '../../Button';
import withControl, { WithControlRenderProps } from '../withControl';

import {
  actionClassName,
  buttonClassName,
  emojiClassName,
  iconClassName,
  menuClassName,
} from './styles';
import { EmojiProps } from './types';

type Props = EmojiProps & WithControlRenderProps;

const Emoji = ({
  children,
  className = '',
  emojiPickerClassName = '',
  onChange,
  ref,
  value,
  ...props
}: Props) => {
  const [isVisible, setVisibility] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const handleClose = useCallback(() => {
    setVisibility(false);
  }, []);

  const onEmojiClick = useCallback(
    (data: EmojiClickData) => {
      const emoji = data.emoji;
      const combined = !value ? emoji : `${value.trim()} ${emoji}`;
      onChange(combined);
      handleClose();
    },
    [handleClose, onChange, value],
  );

  useOutsideClick(buttonRef, menuRef, handleClose);

  const Menu = useMemo(
    () => (
      <div className={menuClassName} ref={menuRef}>
        <EmojiPicker
          className={emojiPickerClassName}
          height={270}
          width={300}
          lazyLoadEmojis
          onEmojiClick={onEmojiClick}
          previewConfig={{ showPreview: false }}
          skinTonesDisabled
        />
      </div>
    ),
    [emojiPickerClassName, onEmojiClick],
  );

  return (
    <>
      <div className={emojiClassName} ref={ref}>
        {isValidElement(children)
          ? cloneElement(children, props as any)
          : children}
        <div
          className={cx(actionClassName, {
            [className]: !!className,
          })}
        >
          <Button
            className={buttonClassName}
            faIcon={faFaceSmile}
            iconClassName={iconClassName}
            onClick={() => setVisibility(!isVisible)}
            ref={buttonRef}
          />
          {isVisible && Menu}
        </div>
      </div>
    </>
  );
};

Emoji.displayName = 'Emoji';

export default withControl(Emoji);
